import { useState } from 'react';
import { useCart } from '../../../context/CartContext';
import type { Product } from '../../../mocks/products';

interface ProductCardProps {
  product: Product;
}

export default function ProductCard({ product }: ProductCardProps) {
  const { addItem } = useCart();
  const [added, setAdded] = useState(false);
  const [showDetail, setShowDetail] = useState(false);

  const handleAdd = () => {
    addItem({
      id: product.id,
      name: product.name,
      price: product.price,
      image: product.image,
      category: product.category,
      size: product.size,
    });
    setAdded(true);
    setTimeout(() => setAdded(false), 1500);
  };

  const discount = Math.round((1 - product.price / product.originalPrice) * 100);

  return (
    <>
      <div className="bg-white rounded-2xl overflow-hidden hover:scale-105 transition-transform duration-200 group cursor-pointer" onClick={() => setShowDetail(true)}>
        <div className="relative overflow-hidden" style={{ height: '230px' }}>
          <img
            src={product.image}
            alt={product.name}
            title={`${product.name} - second hand kids clothing`}
            className="w-full h-full object-cover object-top"
          />
          {product.badge && (
            <span className="absolute top-3 left-3 bg-brand-yellow text-white text-xs font-600 px-2.5 py-1 rounded-full" style={{ fontWeight: 600 }}>
              {product.badge}
            </span>
          )}
          <span className="absolute top-3 right-3 bg-brand-green-light text-brand-green-dark text-xs font-600 px-2.5 py-1 rounded-full" style={{ fontWeight: 600 }}>
            -{discount}%
          </span>
        </div>

        <div className="p-4">
          <p className="text-xs text-gray-400 mb-1">{product.category} · Size {product.size} · {product.condition}</p>
          <h3 className="font-700 text-gray-800 mb-1 text-sm leading-snug" style={{ fontFamily: "'Quicksand', sans-serif", fontWeight: 700 }}>
            {product.name}
          </h3>
          <p className="text-xs text-gray-400 mb-3 line-clamp-2 leading-relaxed">{product.description}</p>

          <div className="flex items-center justify-between">
            <div>
              <span className="text-lg font-700 text-brand-yellow" style={{ fontWeight: 700 }}>${product.price.toFixed(2)}</span>
              <span className="text-xs text-gray-400 line-through ml-2">${product.originalPrice.toFixed(2)}</span>
            </div>
            <button
              onClick={e => { e.stopPropagation(); handleAdd(); }}
              className="flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-600 text-white cursor-pointer transition-all duration-200 whitespace-nowrap"
              style={{ fontWeight: 600, backgroundColor: added ? '#3DBD8A' : '#F5B730' }}
            >
              <i className={added ? 'ri-check-line' : 'ri-shopping-basket-line'}></i>
              {added ? 'Added!' : 'Add to Cart'}
            </button>
          </div>
        </div>
      </div>

      {showDetail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setShowDetail(false)}>
          <div className="bg-white rounded-3xl max-w-2xl w-full overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex flex-col md:flex-row">
              <div className="w-full md:w-1/2" style={{ height: '320px' }}>
                <img src={product.image} alt={product.name} className="w-full h-full object-cover object-top" />
              </div>
              <div className="w-full md:w-1/2 p-7 flex flex-col justify-between">
                <div>
                  <div className="flex items-start justify-between mb-3">
                    <p className="text-xs text-gray-400">{product.category}</p>
                    <button onClick={() => setShowDetail(false)} className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 cursor-pointer transition-colors">
                      <i className="ri-close-line text-gray-600"></i>
                    </button>
                  </div>
                  <h2 className="text-xl font-700 text-gray-800 mb-3" style={{ fontFamily: "'Quicksand', sans-serif", fontWeight: 700 }}>
                    {product.name}
                  </h2>
                  <p className="text-sm text-gray-500 leading-relaxed mb-4">{product.description}</p>

                  <div className="space-y-2">
                    {[
                      { label: 'Size', value: product.size },
                      { label: 'Age', value: product.ageRange },
                      { label: 'Condition', value: product.condition },
                    ].map(item => (
                      <div key={item.label} className="flex items-center gap-2">
                        <span className="text-xs text-gray-400 w-20">{item.label}:</span>
                        <span className="text-xs font-600 text-gray-700 bg-gray-100 px-2.5 py-0.5 rounded-full" style={{ fontWeight: 600 }}>
                          {item.value}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mt-6">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-3xl font-700 text-brand-yellow" style={{ fontWeight: 700 }}>${product.price.toFixed(2)}</span>
                    <span className="text-base text-gray-400 line-through">${product.originalPrice.toFixed(2)}</span>
                    <span className="text-xs bg-brand-green-light text-brand-green-dark px-2 py-0.5 rounded-full font-600" style={{ fontWeight: 600 }}>-{discount}%</span>
                  </div>
                  <button
                    onClick={handleAdd}
                    className="w-full py-3 rounded-full font-600 text-white transition-all duration-200 cursor-pointer whitespace-nowrap"
                    style={{ fontWeight: 600, backgroundColor: added ? '#3DBD8A' : '#F5B730' }}
                  >
                    <i className={`mr-2 ${added ? 'ri-check-line' : 'ri-shopping-basket-line'}`}></i>
                    {added ? 'Added to Cart!' : 'Add to Cart'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
